import React, { useContext, useEffect, useState } from "react";
import { ContentWrapper, DetailsBox, DetailsContent, DetailsRow, DetailsSectionHeader, ItemWrapper } from "../styles";
import Loader from "../../../../components/common/Loader";
import { formatCurrencyAmount } from "../../../../utils/currency";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { clearUserData, setStep } from "../../redux/formDataSlice";
import dayjs from "dayjs";
import { formatValue } from "../../utils";
import WalletOTP from "../../components/SendFunds/components/WalletOTP";
import { sendFromWalletAction } from "../../redux/slice";
import { requestPayoutV2Otp } from "../../api";
import { getErrorMessage } from "../../utils";
import { encryptAlgorithm } from "../../../../utils/encryptionAlgorithm";
import EventFactory from "../../../../utils/eventFactory";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import { Button } from "../../../../components/common/Button";
import { useNavigate, useParams } from "react-router-dom";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { getAllWallets } from "../../api";
import { merchantBusinessKeys } from "../../../Settings/settingsSlice";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";

type ReviewTypes = {
    handlePrevious: () => void;
};

type ReviewState = {
    encryptionKey: string | null;
    publicKey: string;
    taxRefId: string;
    loading: boolean;
    verifyOTP: boolean;
};

type FormData = {
    amount?: number;
    country?: string;
    currency?: string;
    bankCode?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    requestId?: string;
    description?: string;
    type?: string;
    mobileNumber?: string;
    serviceCode?: string;
    recipientEmail?: string;
    otp?: string;
}

const Review = ({ handlePrevious }: ReviewTypes) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { currencyCode } = useParams();
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
    const { businessId } = useAppSelector((state) => state.users);
    const { loading } = useAppSelector((state) => state.wallets);

    const eventFactory = EventFactory.getInstance();

    const [reviewData, setReviewData] = useState<ReviewState>({
        encryptionKey: null,
        publicKey: "",
        taxRefId: "",
        loading: false,
        verifyOTP: false,
    });

    const successAlertContext = useContext(SuccessAlertContext);

    const { onShowAlert: onSuccess } = successAlertContext || {};
    const { onShowAlert } = useContext(NetworkErrorAlertContext);

    const currentDate = dayjs().format("YYYY-MM-DD");

    const updateReviewData = (fields: Partial<ReviewState>) =>
        setReviewData((prev) => ({ ...prev, ...fields }));

    const { bic, accountNumber, bankCode } = getSavedData
    const sortedAccountNumber =
        bankCode === "TZ-TIGO-B2C" || bankCode === "TZ-AIRTEL-B2C"
            ? `255${accountNumber}` : `${bic}:${accountNumber}`

    useEffect(() => {
        if (businessId) {
            dispatch(merchantBusinessKeys(businessId))?.then(({ payload }) => {
                updateReviewData({
                    publicKey: payload?.data?.publicKey ?? "",
                    encryptionKey: payload?.data?.encryptionKey ?? null,
                });
            });
        }
    }, [businessId, dispatch]);

    useEffect(() => {
        updateReviewData({ taxRefId: `tranf-${uuidv4()}` });
    }, []);

    const trackPayout = (status: "success" | "failed", errorMessage?: string) => {
        eventFactory.trackPayout(
            getSavedData?.amount,
            capitalizeCurrencyCode(currencyCode),
            "bank_transfer",
            status,
            errorMessage,
        );
    };

    const handleSendOTP = async () => {
        updateReviewData({ loading: true });
        try {
            await requestPayoutV2Otp(reviewData.taxRefId, reviewData.publicKey);
            updateReviewData({ verifyOTP: true, loading: false });
            eventFactory.captureEvent("payout_otp", {
                status: "success",
                currency: capitalizeCurrencyCode(currencyCode),
            });
        } catch (err: any) {
            updateReviewData({ loading: false });
            const message = getErrorMessage(err, "Failed to send OTP");
            onShowAlert?.("", message);
            eventFactory.captureEvent("payout_otp", {
                status: "failure",
                failure_reason: message,
                currency: capitalizeCurrencyCode(currencyCode) || "",
            });
        }
    };

    const handlePayout = async (otp?: string) => {
        const payloadData: Partial<FormData> = {
            amount: getSavedData?.amount,
            country: "TZ",
            currency: "TZS",
            type: "Personal",
            serviceCode: getSavedData?.bankCode,
            accountNumber: sortedAccountNumber,
            accountName: getSavedData?.accountName,
            requestId: reviewData.taxRefId,
            description: getSavedData?.paymentDescription,
            otp,
        };

        const encryptedPayloadData = encryptAlgorithm(
            payloadData,
            reviewData.encryptionKey,
        );
        const payload = { message: encryptedPayloadData };

        const actionResult = dispatch(
            sendFromWalletAction({ data: payload, token: reviewData.publicKey }),
        );

        actionResult.then((result) => {
            if (sendFromWalletAction.fulfilled.match(result)) {
                getAllWallets({ businessId });
                onSuccess("Payout successfully sent");
                trackPayout("success");
                dispatch(clearUserData());
                navigate(`/wallets/`);
            } else if (sendFromWalletAction.rejected.match(result)) {
                onShowAlert("", (result.payload as string) ?? "Failed to send from wallet.");
                trackPayout("failed", (result.payload as string) ?? result.error.message);
                Sentry.captureException(sendFromWalletAction);
            }
        });
    };

    return (
        <ContentWrapper>
            <Loader isLoading={reviewData.loading || loading} />
            {reviewData.verifyOTP && (
                <WalletOTP
                    setVerifyOTP={(v) => updateReviewData({ verifyOTP: v })}
                    makePayment={handlePayout}
                    mode="transaction"
                    onResend={handleSendOTP}
                />
            )}

            <DetailsBox>
                <div>
                    <DetailsSectionHeader>
                        <h1>Transaction details</h1>
                        <span onClick={() => dispatch(setStep(0))}>Edit</span>
                    </DetailsSectionHeader>
                    <DetailsContent>
                        <DetailsRow>
                            <p>Amount sent</p>
                            <span>
                                {formatCurrencyAmount("TZS", getSavedData?.amount)}
                            </span>
                        </DetailsRow>
                        <DetailsRow>
                            <p>Processing Fee</p>
                            <span>{formatCurrencyAmount("TZS", getSavedData?.fees)}</span>
                        </DetailsRow>
                        {getSavedData?.description && (
                            <DetailsRow>
                                <p>Description</p>
                                <span>{getSavedData?.description}</span>
                            </DetailsRow>
                        )}

                        <DetailsRow>
                            <p>Total amount charged</p>
                            <span>{formatCurrencyAmount("TZS", getSavedData?.totalAmount)}</span>
                        </DetailsRow>
                        <DetailsRow>
                            <p>Date</p>
                            <span>{currentDate}</span>
                        </DetailsRow>
                    </DetailsContent>
                </div>

                <div>
                    <DetailsSectionHeader>
                        <h1>Beneficiary details</h1>
                        <span onClick={() => dispatch(setStep(1))}>Edit</span>
                    </DetailsSectionHeader>
                    <DetailsContent>
                        <DetailsRow>
                            <p>Beneficiary name</p>
                            <span>{formatValue(getSavedData?.accountName)}</span>
                        </DetailsRow>

                        <DetailsRow>
                            <p>
                                Account number
                            </p>
                            <span>{sortedAccountNumber}</span>
                        </DetailsRow>


                        <DetailsRow>
                            <p>
                                Bank name
                            </p>
                            <span>{getSavedData?.bankName}</span>
                        </DetailsRow>
                    </DetailsContent>
                </div>
            </DetailsBox>

            <ItemWrapper>
                <Button
                    onClick={handlePrevious}
                    label="Prev"
                    height="48px"
                    theme="alternate"
                    width="203px"
                />

                <Button
                    label="Continue"
                    height="48px"
                    onClick={handleSendOTP}
                />
            </ItemWrapper>

        </ContentWrapper>
    )
}

export default Review